import React from 'react';
import { CheckCircle2, XCircle, AlertCircle, HelpCircle, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';

type ScoreValue = string | number | boolean | ScoreData;

interface ScoreData {
  [key: string]: ScoreValue;
}

interface CallScorecardProps {
  data: {
    call_type: string;
    global_scoring: ScoreData;
    sales_scoring?: ScoreData;
    _metrics?: ScoreData;
  };
}

const BooleanIcon: React.FC<{ value: ScoreValue }> = ({ value }) => {
  if (value === true || value === 'true')
    return <CheckCircle2 className="w-4 h-4 text-green-700 dark:text-green-300" />;
  if (value === false || value === 'false') return <XCircle className="w-4 h-4 text-red-600" />;
  return <HelpCircle className="w-4 h-4 text-gray-400" />;
};

const ObjectionIcon: React.FC<{ value: string }> = ({ value }) => {
  if (value === 'Addressed') return <CheckCircle2 className="w-4 h-4 text-gray-700 dark:text-gray-300" />;
  if (value === 'Not addressed') return <AlertCircle className="w-4 h-4 text-yellow-600" />;
  if (value === 'Not raised') return <HelpCircle className="w-4 h-4 text-gray-400" />;
  return null;
};

const ScoreItem: React.FC<{ label: string; value: ScoreValue }> = ({ label, value }) => {
  const formattedLabel = label.replace(/_/g, ' ');

  if (typeof value === 'object' && value !== null) {
    return (
      <div className="mb-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{formattedLabel}</h4>
        <div className="pl-3 border-l-2 border-gray-300 dark:border-gray-600">
          {Object.entries(value).map(([subKey, subValue]) => (
            <ScoreItem key={subKey} label={subKey} value={subValue} />
          ))}
        </div>
      </div>
    );
  }

  if (typeof value === 'boolean' || value === 'true' || value === 'false') {
    return (
      <div className="flex items-center justify-between py-1 text-sm">
        <span className="mr-2 text-gray-700 dark:text-gray-300">{formattedLabel}</span>
        <BooleanIcon value={value} />
      </div>
    );
  }

  if (typeof value === 'number') {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-700 dark:text-gray-300">{formattedLabel}</span>
          <span className="font-medium text-gray-700 dark:text-gray-300">{value}/5</span>
        </div>
        <Progress value={value * 20} className="h-2 bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  if (value === 'Addressed' || value === 'Not addressed' || value === 'Not raised') {
    return (
      <div className="flex items-center justify-between py-1 text-sm">
        <span className="mr-2 text-gray-700 dark:text-gray-300">{formattedLabel}</span>
        <ObjectionIcon value={value} />
      </div>
    );
  }

  return (
    <div className="flex flex-col py-1 text-sm">
      <span className="text-gray-700 dark:text-gray-300">{formattedLabel}</span>
      <span className="font-medium text-gray-700 dark:text-gray-300">{value}</span>
    </div>
  );
};

const ScoreSection: React.FC<{ title: string; data: ScoreData }> = ({ title, data }) => {
  return (
    <Card className="w-full mb-4 overflow-hidden shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800">
        <CardTitle className="text-base font-semibold text-gray-800 dark:text-gray-200">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-2">
        <div className="space-y-2">
          {Object.entries(data).map(([key, value], index, array) => (
            <React.Fragment key={key}>
              <ScoreItem label={key} value={value} />
              {index < array.length - 1 && <Separator className="my-2" />}
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const DynamicCallScorecard: React.FC<CallScorecardProps> = ({ data }) => {
  // Ensure data is an object
  const dataObject = Array.isArray(data) ? data[0] : data;

  return (
    <div className="space-y-4 bg-white rounded-lg dark:bg-gray-800">
      {Object.entries(dataObject).map(([key, value]) => {
        if (key === 'call_type' || key === '_metrics') return null;
        if (typeof value === 'object' && value !== null) {
          return <ScoreSection key={key} title={key.replace(/_/g, ' ')} data={value as ScoreData} />;
        }
        return null;
      })}

      {dataObject._metrics && (
        <Card className="w-full overflow-hidden shadow-sm">
          <CardHeader className="flex flex-row items-center px-4 py-3 space-x-2 bg-gray-50 dark:bg-gray-800">
            <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <CardTitle className="text-base font-semibold text-gray-700 dark:text-gray-300">Metrics</CardTitle>
          </CardHeader>
          <CardContent className="px-4 py-2">
            <div className="space-y-2">
              {Object.entries(dataObject._metrics).map(([key, value], index, array) => (
                <React.Fragment key={key}>
                  <ScoreItem label={key} value={value as ScoreData} />
                  {index < array.length - 1 && <Separator className="my-2" />}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DynamicCallScorecard;
